import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import SingleSelect from '../../components/SingleSelect';
import DisplayDate from '../../components/DisplayDate';
import GlobalModal from '../../components/GlobalModal';
import EditLogoModal from './EditLogoModal';
import CancelPlanModal from './CancelPlanModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import OrganisationLogo from '../../components/OrganisationLogo';
import UpdateNumberOfChildrenModal from './UpdateNumberOfChildrenModal';
import FormGroupWithError from '../../components/FormGroupWithError';

// ICONS
import { ReactComponent as BillChildrenIcon } from '../../assets/svg/bill-children.svg';
import { ReactComponent as BillReceiptIcon } from '../../assets/svg/bill-receipt.svg';
import { ReactComponent as EditIcon } from '../../assets/svg/edit.svg';
import { ReactComponent as DownloadIcon } from '../../assets/svg/download.svg';

// TYPES
import {
	OrganisationSettingsOrganisationDetails,
	OrganisationAddressCommand,
	DomainPaymentStatus
} from '../../api/models';

const Settings: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Get all States Names from Redux Store
	const states = useAppSelector((state) => state.lists.states);

	// Check if user is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Get all Payment Plans from Redux Store
	const paymentPlans = useAppSelector((state) => state.lists.paymentPlans);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// States to control Modal
	const [showEditLogoModal, setShowEditLogoModal] = React.useState<boolean>(false);

	const [showCancelPlanModal, setShowCancelPlanModal] = React.useState<boolean>(false);

	const [showUpdateNumberOfChildrenModal, setShowUpdateNumberOfChildrenModal] = React.useState<boolean>(false);

	// ORGANISATION DETAIL DATA FETCH
	const initialOrganisationDetail: OrganisationSettingsOrganisationDetails = {
		id: undefined,
		identifier: undefined,
		name: undefined,
		logo: undefined,
		australianBusinessNumber: undefined,
		numberOfChildren: undefined,
		canCancelPlan: undefined,
		planType: undefined,
		cost: undefined,
		unitCost: undefined,
		paymentCycle: undefined,
		nextInvoiceDate: undefined,
		paymentHistory: []
	};

	const [organisationDetail, setOrganisationDetail] =
		React.useState<OrganisationSettingsOrganisationDetails>(initialOrganisationDetail);

	const getOrganisationDetail = async () => {
		setIsLoading(true);

		const response = await apibridge.getOrganisationDetail();

		imageVersion.current = imageVersion.current + 1;

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialOrganisationDetail } = response;

			setIsLoading(false);

			setOrganisationDetail(result);
		}
	};

	// ORGANISATION ADDRESS DATA FETCH
	const initialOrganisationAddress: OrganisationAddressCommand = {
		organisationAddress: undefined,
		sameAddresses: undefined,
		email: undefined,
		phoneNumber: undefined,
		mailingAddress: undefined
	};

	const [organisationAddressData, setOrganisationAddressData] =
		React.useState<OrganisationAddressCommand>(initialOrganisationAddress);

	const getOrganisationAddress = async () => {
		setIsLoading(true);

		const response = await apibridge.getOrganisationAddress();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialOrganisationAddress } = response;

			setIsLoading(false);

			setOrganisationAddressData(result);
		}
	};

	// Function to combine 2 fetch call
	const getSettingsData = async () => {
		setIsLoading(true);

		await getOrganisationDetail();

		await getOrganisationAddress();

		setIsLoading(false);
	};

	React.useEffect(() => {
		getSettingsData();
	}, []);

	const { organisationAddress, mailingAddress, sameAddresses, email, phoneNumber } = organisationAddressData;

	const {
		name,
		logo,
		australianBusinessNumber,
		numberOfChildren,
		canCancelPlan,
		planType,
		cost,
		unitCost,
		paymentCycle,
		nextInvoiceDate,
		paymentHistory
	} = organisationDetail;

	// Logic to load current payment Icon first to avoid slow loading of the icon itself
	const [currentPaymentIconPath, setCurrentPaymentIconPath] = React.useState<string>('');

	React.useEffect(() => {
		const currentPaymentPlan = paymentPlans?.find((item) => item.label === planType);

		const currentPaymentIcon = currentPaymentPlan?.icon;

		setCurrentPaymentIconPath(`/assets/${currentPaymentIcon}`);
	}, [planType]);

	// STATE TO CONTROL ACTIVE TAB
	const [activeTab, setActiveTab] = React.useState<string>('tab1');

	// FORMS SETUP
	const onSubmit = async (values: any) => {
		setIsLoading(true);

		const response = await apibridge.submitOrganisationAddress(values);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			getSettingsData();

			setIsLoading(false);
		}
	};

	// FORM VALIDATION FOR AUSTRALIAN PHONE NUMBERS
	const phoneRegExp =
		/^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/;

	const validationSchema = Yup.object({
		organisationAddress: Yup.object({
			address1: Yup.string().required('Required'),
			city: Yup.string().required('Required'),
			state: Yup.string().required('Required'),
			postcode: Yup.string()
				.required('Required')
				.matches(/^[\d]+$/, 'Only numbers are allowed')
				.length(4, 'Postcode must be 4 numbers')
		}),
		mailingAddress: Yup.object({
			address1: Yup.string().when('sameAddresses', {
				is: false,
				then: Yup.string().required('Required')
			}),
			city: Yup.string().when('sameAddresses', {
				is: false,
				then: Yup.string().required('Required')
			}),
			postcode: Yup.string().when('sameAddresses', {
				is: false,
				then: Yup.string()
					.required('Required')
					.matches(/^[\d]+$/, 'Only numbers are allowed')
					.length(4, 'Postcode must be 4 numbers')
			}),
			state: Yup.string().when('sameAddresses', {
				is: false,
				then: Yup.string().required('Required')
			})
		}),
		phoneNumber: Yup.string().matches(phoneRegExp, 'Phone number is not valid'),
		email: Yup.string().required('Required')
	});

	const initialValues = {
		organisationAddress,
		mailingAddress,
		sameAddresses,
		phoneNumber,
		email
	};

	// Set up version to reload the image since the image url does not change and browswer will not reload/
	const imageVersion = React.useRef<any>(1);

	// Get stripe Url from api call and redirect to that link
	const updatePaymentDetail = async () => {
		setIsLoading(true);

		const response = await apibridge.getPaymentUpdateLink();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			const { result = {} } = response;

			const paymentUrl = result.url || '';

			window.location.href = paymentUrl;
		}
	};

	return (
		<div className="page-general-layout settings-page">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<OrganisationLogo src={logo + '&v=' + imageVersion.current} alt="organisation logo" />

								<h2 className="header-text settings-page">{name}</h2>

								<div className="general-info">
									<span className="header-info">{planType}</span>

									<div className="header-dot"></div>

									<span className="header-info">{australianBusinessNumber}</span>
								</div>

								{isOrganisationActive && (
									<Button
										onClick={() => setShowEditLogoModal(true)}
										className="btn-text-icon mtop-20 mbtom-60"
										buttonIcon={<EditIcon className="link-icon" />}
									>
										Edit logo
									</Button>
								)}

								<nav className="page-header-section settings-page">
									<div className="header-left">
										<Button
											onClick={() => setActiveTab('tab1')}
											className={`staff-category settings-page pdl-0 ${activeTab === 'tab1' ? 'active' : ''}`}
										>
											Organisation details
										</Button>

										<Button
											onClick={() => {
												setActiveTab('tab2');
												getSettingsData();
											}}
											className={`staff-category settings-page ${activeTab === 'tab2' ? 'active' : ''}`}
										>
											Payment & Billing
										</Button>
									</div>
								</nav>

								{activeTab === 'tab1' && (
									<Formik
										initialValues={initialValues}
										validationSchema={validationSchema}
										onSubmit={onSubmit}
										enableReinitialize
									>
										{(formik) => {
											return (
												<Form translate="yes" className="general-form" autoComplete="off">
													<article className="children-quantity">
														<label htmlFor="numberOfChildrenInput" className="children-quantity-title">
															Number of children at your organisation
														</label>

														<div className="number-container">
															<div className="quantity-input">{numberOfChildren}</div>

															{isOrganisationAdmin && isOrganisationActive && (
																<Button
																	className="btn-primary w-100 mtop-16"
																	onClick={() => setShowUpdateNumberOfChildrenModal(true)}
																>
																	Update
																</Button>
															)}
														</div>
													</article>

													<section className="organisation-address">
														<h2 className="organisation-address-title">Organisation address</h2>

														<label htmlFor="organisationAddress.address1" className="label">
															Address Line 1
														</label>

														<div className="form-field">
															<Field
																name="organisationAddress.address1"
																id="organisationAddress.address1"
																type="text"
																className="form-input"
																disabled={isOrganisationActive === false}
															/>

															<ErrorMessage
																name="organisationAddress.address1"
																component="div"
																className="error-message"
															/>
														</div>

														<label htmlFor="organisationAddress.address2" className="label">
															Address Line 2
														</label>

														<div className="form-field">
															<Field
																name="organisationAddress.address2"
																id="organisationAddress.address2"
																type="text"
																className="form-input"
																disabled={isOrganisationActive === false}
															/>

															<ErrorMessage
																name="organisationAddress.address2"
																component="div"
																className="error-message"
															/>
														</div>

														<div className="row">
															<div className="col-12 col-lg-4">
																<label htmlFor="organisationAddress.city" className="label">
																	City
																</label>

																<div className="form-field">
																	<Field
																		name="organisationAddress.city"
																		id="organisationAddress.city"
																		type="text"
																		className="form-input"
																		disabled={isOrganisationActive === false}
																	/>

																	<ErrorMessage
																		name="organisationAddress.city"
																		component="div"
																		className="error-message"
																	/>
																</div>
															</div>

															<div className="col-8 col-lg-4">
																<label htmlFor="organisationAddress.state" className="label">
																	State/Territory
																</label>

																<SingleSelect
																	options={states || []}
																	name="organisationAddress.state"
																	value={formik.values.organisationAddress?.state}
																	onChange={(val: string) => formik.setFieldValue('organisationAddress.state', val)}
																	isDisabled={isOrganisationActive === false}
																/>

																<ErrorMessage
																	name="organisationAddress.state"
																	component="div"
																	className="error-message"
																/>
															</div>

															<div className="col-4 col-lg-4">
																<label htmlFor="organisationAddress.postcode" className="label">
																	Postcode
																</label>

																<div className="form-field">
																	<Field
																		id="organisationAddress.postcode"
																		name="organisationAddress.postcode"
																		type="number"
																		className="form-input"
																		disabled={isOrganisationActive === false}
																	/>

																	<ErrorMessage
																		name="organisationAddress.postcode"
																		component="div"
																		className="error-message"
																	/>
																</div>
															</div>
														</div>

														<div className="row">
															<div className="col-12">
																<label htmlFor="email" className="label">
																	Email
																</label>

																<div className="form-field">
																	<Field
																		name="email"
																		id="email"
																		type="text"
																		className="form-input"
																		disabled={isOrganisationActive === false}
																	/>

																	<ErrorMessage name="email" component="div" className="error-message" />
																</div>
															</div>
														</div>

														<div className="row">
															<div className="col-12 col-lg-4">
																<label htmlFor="phoneNumber" className="label">
																	Phone Number
																</label>

																<div className="form-field">
																	<Field
																		name="phoneNumber"
																		id="phoneNumber"
																		type="text"
																		className="form-input"
																		disabled={isOrganisationActive === false}
																	/>
																	<p className="text-subtitle">
																		For a landline, please include an area code e.g. 03 XXX
																	</p>
																	<ErrorMessage name="phoneNumber" component="div" className="error-message" />
																</div>
															</div>
														</div>
													</section>

													<FormGroupWithError name="sameAddresses" className="acknowledge-container mtop-30 mbtom-0">
														<label className="form-control-checkbox">
															<Field name="sameAddresses" type="checkbox" />

															<span>Mailing address is the same as above</span>
														</label>
													</FormGroupWithError>

													{formik.values.sameAddresses === false && (
														<section className="organisation-address">
															<h2 className="organisation-address-title">Mailing address</h2>

															<label htmlFor="mailingAddress.address1" className="label">
																Address Line 1
															</label>

															<div className="form-field">
																<Field
																	name="mailingAddress.address1"
																	id="mailingAddress.address1"
																	type="text"
																	className="form-input"
																	disabled={isOrganisationActive === false}
																/>

																<ErrorMessage
																	name="mailingAddress.address1"
																	component="div"
																	className="error-message"
																/>
															</div>

															<label htmlFor="mailingAddress.address2" className="label">
																Address Line 2
															</label>

															<div className="form-field">
																<Field
																	name="mailingAddress.address2"
																	id="mailingAddress.address2"
																	type="text"
																	className="form-input"
																	disabled={isOrganisationActive === false}
																/>

																<ErrorMessage
																	name="mailingAddress.address2"
																	component="div"
																	className="error-message"
																/>
															</div>

															<div className="row">
																<div className="col-12 col-lg-4">
																	<label htmlFor="mailingAddress.city" className="label">
																		City
																	</label>

																	<div className="form-field">
																		<Field
																			name="mailingAddress.city"
																			id="mailingAddress.city"
																			type="text"
																			className="form-input"
																			disabled={isOrganisationActive === false}
																		/>

																		<ErrorMessage
																			name="mailingAddress.city"
																			component="div"
																			className="error-message"
																		/>
																	</div>
																</div>

																<div className="col-8 col-lg-4">
																	<label htmlFor="mailingAddress.state" className="label">
																		State/Territory
																	</label>

																	<SingleSelect
																		options={states || []}
																		name="mailingAddress.state"
																		value={formik.values.mailingAddress?.state}
																		onChange={(val: string) => formik.setFieldValue('mailingAddress.state', val)}
																		isDisabled={isOrganisationActive === false}
																	/>

																	<ErrorMessage name="mailingAddress.state" component="div" className="error-message" />
																</div>

																<div className="col-4 col-lg-4">
																	<label htmlFor="mailingAddress.postcode" className="label">
																		Postcode
																	</label>

																	<div className="form-field">
																		<Field
																			id="mailingAddress.postcode"
																			name="mailingAddress.postcode"
																			type="number"
																			className="form-input"
																			disabled={isOrganisationActive === false}
																		/>

																		<ErrorMessage
																			name="mailingAddress.postcode"
																			component="div"
																			className="error-message"
																		/>
																	</div>
																</div>
															</div>
														</section>
													)}

													{isOrganisationActive && (
														<div className="form-button-group">
															<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
																Save changes
															</Button>
														</div>
													)}
												</Form>
											);
										}}
									</Formik>
								)}

								{activeTab === 'tab2' && (
									<div className="billing-tab">
										<div className="billing-summary">
											<div className="billing-plan">
												<img src={currentPaymentIconPath} className="bill-icon" />

												<h2 className="bill-title">{planType}</h2>

												<p className="bill-detail">
													{cost && <>${cost} </>}

													<span className="bill-info">{paymentCycle === 'Monthly' ? '/month' : '/year'} excl. GST</span>
												</p>

												{isOrganisationActive && canCancelPlan && (
													<Button className="btn-text btn-cancel" onClick={() => setShowCancelPlanModal(true)}>
														Cancel plan
													</Button>
												)}

												<a className="btn-text btn-update-payment" onClick={updatePaymentDetail}>
													Update Payment Detail
												</a>
											</div>

											<div className="billing-quantity">
												<BillChildrenIcon className="bill-icon" />

												{numberOfChildren && (
													<>
														<h2 className="bill-title">{numberOfChildren}</h2>

														<p className="bill-detail">Children</p>
													</>
												)}

												{unitCost && <p className="bill-info">${unitCost} per child, per year</p>}
											</div>

											<div className="billing-date">
												<BillReceiptIcon className="bill-icon" />

												{nextInvoiceDate && (
													<>
														<h2 className="bill-title">
															<DisplayDate>{nextInvoiceDate}</DisplayDate>
														</h2>

														<p className="bill-detail">Next invoice issue date</p>
													</>
												)}

												{paymentCycle && <p className="bill-info">Billed {paymentCycle}</p>}
											</div>
										</div>

										{paymentHistory && paymentHistory.length > 0 && (
											<div className="payment-history">
												<h2 className="payment-title">Payment History</h2>

												<div className="row table-header">
													<div className="col-5 col-lg-6 col-name">Date</div>

													<div className="col-3 col-lg-2 col-name">Amount</div>

													<div className="col-3 col-lg-2 col-name">Status</div>
												</div>

												<ul>
													{paymentHistory.map((item, index) => {
														return (
															<li key={index} className="row table-row">
																<div className="col-5 col-lg-6 payment-date">
																	<DisplayDate>{item.date}</DisplayDate>
																</div>

																{item.amount && <div className="col-3 col-lg-2 payment-amount">${item.amount}</div>}

																<div
																	className={`col-3 col-lg-2 payment-status ${
																		item.status === DomainPaymentStatus.Failed ? 'payment-failed' : null
																	}`}
																>
																	{item.statusDescription}
																</div>

																<div className="col-1 col-lg-2 payment-download">
																	{item.downloadLink && (
																		<>
																			<a className="btn-text d-none d-md-block" href={item.downloadLink} download>
																				Download
																			</a>

																			<a className="btn-icon d-flex d-md-none" download href={item.downloadLink}>
																				<DownloadIcon />
																			</a>
																		</>
																	)}
																</div>
															</li>
														);
													})}
												</ul>
											</div>
										)}
									</div>
								)}

								<GlobalModal isOpen={showEditLogoModal} setIsOpen={setShowEditLogoModal} titleText="Edit your logo">
									<EditLogoModal getSettingsData={getSettingsData} />
								</GlobalModal>

								<GlobalModal
									isOpen={showCancelPlanModal}
									setIsOpen={setShowCancelPlanModal}
									titleText="Cancel your plan"
								>
									<CancelPlanModal />
								</GlobalModal>

								<GlobalModal
									isOpen={showUpdateNumberOfChildrenModal}
									setIsOpen={setShowUpdateNumberOfChildrenModal}
									titleText="Update Number of Children"
								>
									<UpdateNumberOfChildrenModal numberOfChildren={numberOfChildren} getSettingsData={getSettingsData} />
								</GlobalModal>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default Settings;
